<template lang="pug">
  div.full-width.mt-n12
    div.d-flex.flex-grow
      v-row.header-filter
        v-col(fluid)
          v-spacer
        v-col(sm=3)
          div.media-date-picker.mt-4
            DateRangePicker(
              :placeholder="$t('table.globalMedia.date')",
              :value.sync="dates",
              @update:value="fetch",
            )
        v-col(sm=3)
          v-select#media-tag-filter(
            clearable,
            multiple,
            hide-details,
            v-model="media_tags",
            item-value="id",
            item-text="name",
            :items="mediaTags",
            :label="$t('table.globalMedia.tag')",
            @change="filterTag",
          )
        v-col(sm=4)
          div.pr-2.d-flex.align-end
            FilterBox.mr-2(
              :label="$t('table.globalMedia.filter')",
              :search.sync="search",
              @update:search="fetch()",
            )
            v-btn.ml-1(
              fab,
              small,
              depressed,
              v-if="isSuperadmin",
              color="primary",
              @click="exportData",
            )
              v-icon fas fa-file-download
    v-overlay(
      v-if="isLoading",
      :value="isLoading",
    )
      v-progress-circular(:value="20" indeterminate)
    v-row
      v-col.media(
        v-for="media in items",
        :key="media.id",
        md="3", sm="4", xs="12",
      )
        v-card.media_item(:class="{ 'media_video': media.is_video }")
          Projector(
            show-additional-info,
            allow-video-toggle,
            show-mask,
            :media="media",
            @preview="previewMedia(media)",
            @queryDate="queryDate",
            @querySearch="querySearch",
            @queryTag="queryTag",
          )
    .text-center
      v-btn(
        v-if="count > items.length",
        :loading="isLoading",
        @click="loadMore",
      ) Load more...
    Previewer(
      v-if="previewer",
      :selections="selections",
      :value.sync="previewer",
    )
    Controller(
      hide-edit-controls,
      :selections="selections",
      @delete="remove",
      @download="download",
      @edit="$refs.uploadBtn.click()",
      @preview="previewer = true",
      @remove="previewMedia",
      @showLogs="showLogs",
    )
</template>

<script>
import { mapGetters } from "vuex";
import { mergeRight, without } from "ramda";
import { endOfDay, startOfDay, subDurationFromToday } from "@/utils/time";
import {
  exportCSV,
  imageViewer,
  progressWrapper,
  roleManager,
} from "@/config/mixins.js";

import Controller from "@/components/Media/Controller.vue";
import DateRangePicker from "@/components/forms/inputs/DateRangePicker.vue";
import FilterBox from "@/components/table/FilterBox.vue";
import Previewer from "@/components/Media/Previewer.vue";
import Projector from "@/components/Media/Projector.vue";

export default {
  components: {
    Controller,
    DateRangePicker,
    FilterBox,
    Previewer,
    Projector,
  },

  mixins: [exportCSV, imageViewer, progressWrapper, roleManager],

  data: () => ({
    dates: [subDurationFromToday({ months: 1 }), new Date()],
    media_tags: [],
    search: null,
    tag_timeout: null,
  }),

  computed: {
    ...mapGetters({
      count: "media/count",
      items: "media/items",
      mediaTags: "mediaTag/items",
    }),
  },

  created() {
    this.$store.dispatch("media/flush");
    this.$store.dispatch("mediaTag/fetch", {
      sortBy: "name",
      sortDesc: false,
      limit: 50,
      hardFetch: true,
    });
    this.fetch();
  },

  methods: {
    queryDate(date) {
      const [dd, mm, yyyy] = date.split("-");
      this.date = [dd, mm, yyyy].join("/");
      this.fetch();
    },

    querySearch(search) {
      this.search = search;
      this.fetch();
    },

    queryTag(tag) {
      if (this.media_tags.includes(tag)) {
        this.media_tags = without([tag], this.media_tags);
      } else {
        this.media_tags.push(tag);
      }
      this.fetch();
    },

    filterTag() {
      if (this.tag_timeout) {
        clearTimeout(this.tag_timeout);
      }

      this.tag_timeout = setTimeout(() => {
        this.fetch({});
      }, 500);
    },

    loadMore() {
      this.fetch({ skip: this.items.length, limit: 12 });
    },

    getParams() {
      const params = {
        search: this.search,
        media_tags: this.media_tags,
        sortBy: "date",
        sortDesc: true,
      };

      if (Array.isArray(this.dates) && this.dates[0] && this.dates[1]) {
        const [startDate, endDate] = this.dates;

        params.start_date = startOfDay(startDate).toISOString();
        params.end_date = endOfDay(endDate).toISOString();
      } else {
        delete params.start_date;
        delete params.end_date;
      }

      return params;
    },

    fetch(extraParams = { skip: 0, limit: 12 }) {
      this.wrapProgress(() =>
        this.$store.dispatch(
          "media/fetch",
          mergeRight(this.getParams(), extraParams)
        )
      );
    },

    exportData() {
      this.exportCSV({
        type: "media",
        fileName: "medias.csv",
        params: this.getParams(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.media-date-picker {
  width: 290px;
  margin-left: auto;
}
</style>
